// src/components/StepProgressCompact.jsx
import React from "react";
import PropTypes from "prop-types";

/**
 * Compact responsive stepper (center aligned)
 * Props:
 *  - activeStep: 1|2|3 (which step is active)
 *  - labels: optional array of 3 strings (defaults provided)
 */
export default function StepProgress({
  activeStep = 1,
  labels = ["Select Plan", "Choose Preferred Slot", "Add Details"],
}) {
  const id = Math.round(Math.random() * 1e6);

  return (
    <div
      aria-label="Booking steps"
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        margin: "15px 0",
        marginBottom: "0px",
      }}
    >
      {/* scoped CSS for responsive compact styling */}
      <style>{`
        .spc-${id} {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          flex-wrap: wrap;
          max-width: 720px;
          padding: 6px 10px;
          box-sizing: border-box;
          text-align: center;
        }

        .spc-${id} .step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          min-width: 70px;
        }

        .spc-${id} .circle {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          font-weight: 700;
          font-size: 14px;
          flex-shrink: 0;
        }

        .spc-${id} .label {
          font-size: 12px;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
        }

        .spc-${id} .connector {
          width: 36px;
          height: 2px;
          background: #e6e6e6;
          border-radius: 2px;
          flex-shrink: 0;
        }

        .spc-${id} .completed { background: #14a077; color: #fff; }
        .spc-${id} .active { background: #14a077; color: #fff; box-shadow: 0 4px 10px rgba(20,160,119,0.15); }
        .spc-${id} .disabled { background: #f2f2f2; color: #777; border: 1px solid #e6e6e6; }

        @media (max-width: 480px) {
          .spc-${id} {
            gap: 6px;
            padding: 4px 6px;
          }
          .spc-${id} .circle {
            width: 28px;
            height: 28px;
            font-size: 12px;
          }
          .spc-${id} .label {
            font-size: 11px;
          }
          .spc-${id} .connector {
            width: 20px;
          }
        }
      `}</style>

      <div className={`spc-${id}`} role="list">
        {labels.map((lbl, i) => {
          const step = i + 1;
          const state =
            step < activeStep
              ? "completed"
              : step === activeStep
              ? "active"
              : "disabled";

          return (
            <React.Fragment key={step}>
              <div
                className="step"
                role="listitem"
                aria-current={state === "active" ? "step" : undefined}
              >
                <div
                  className={`circle ${state}`}
                  aria-hidden="true"
                  title={`${lbl} — ${state}`}
                >
                  {state === "completed" ? "✓" : step}
                </div>
                <div className="label">{lbl}</div>
              </div>

              {step < labels.length && (
                <div
                  className="connector"
                  aria-hidden="true"
                  style={{
                    background:
                      step < activeStep ? "#14a077" : "#e6e6e6",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

StepProgress.propTypes = {
  activeStep: PropTypes.number,
  labels: PropTypes.arrayOf(PropTypes.string),
};